import { clamp } from "@/utility/clamp";

const DEFAULT_ENERGY_PLOT_MODEL_COLOR = "#094e98";

/**
 * Converts a 3-digit or 6-digit hex color into RGB channels.
 * Returns `null` if the value is not a valid hex color.
 */
const hexToRgb = (hexColor: string) => {
  const normalizedHex = hexColor.trim();
  const shortHexMatch = normalizedHex.match(/^#([0-9a-fA-F]{3})$/);
  const fullHexMatch = normalizedHex.match(/^#([0-9a-fA-F]{6})$/);

  if (shortHexMatch) {
    const expanded = shortHexMatch[1]
      .split("")
      .map((part) => `${part}${part}`)
      .join("");
    const numericValue = Number.parseInt(expanded, 16);
    return {
      r: (numericValue >> 16) & 255,
      g: (numericValue >> 8) & 255,
      b: numericValue & 255,
    };
  }

  if (fullHexMatch) {
    const numericValue = Number.parseInt(fullHexMatch[1], 16);
    return {
      r: (numericValue >> 16) & 255,
      g: (numericValue >> 8) & 255,
      b: numericValue & 255,
    };
  }

  return null;
};

/** Converts RGB values to a clamped 6-digit hex color string. */
const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map((value) => clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0")).join("")}`;

/** Converts RGB color channels (0-255) to HSL values (h: 0-360, s/l: 0-100). */
const rgbToHsl = (r: number, g: number, b: number) => {
  const normalizedR = r / 255;
  const normalizedG = g / 255;
  const normalizedB = b / 255;
  const max = Math.max(normalizedR, normalizedG, normalizedB);
  const min = Math.min(normalizedR, normalizedG, normalizedB);
  const delta = max - min;
  const lightness = (max + min) / 2;
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

  let hue = 0;
  if (delta !== 0) {
    if (max === normalizedR) {
      hue = 60 * (((normalizedG - normalizedB) / delta) % 6);
    } else if (max === normalizedG) {
      hue = 60 * ((normalizedB - normalizedR) / delta + 2);
    } else {
      hue = 60 * ((normalizedR - normalizedG) / delta + 4);
    }
  }

  return {
    h: (hue + 360) % 360,
    s: saturation * 100,
    l: lightness * 100,
  };
};

/** Converts HSL values (h: 0-360, s/l: 0-100) to RGB channels (0-255). */
const hslToRgb = (h: number, s: number, l: number) => {
  const normalizedS = clamp(s, 0, 100) / 100;
  const normalizedL = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * normalizedL - 1)) * normalizedS;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = normalizedL - c / 2;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (h >= 0 && h < 60) {
    rPrime = c;
    gPrime = x;
  } else if (h >= 60 && h < 120) {
    rPrime = x;
    gPrime = c;
  } else if (h >= 120 && h < 180) {
    gPrime = c;
    bPrime = x;
  } else if (h >= 180 && h < 240) {
    gPrime = x;
    bPrime = c;
  } else if (h >= 240 && h < 300) {
    rPrime = x;
    bPrime = c;
  } else {
    rPrime = c;
    bPrime = x;
  }

  return {
    r: (rPrime + m) * 255,
    g: (gPrime + m) * 255,
    b: (bPrime + m) * 255,
  };
};

/**
 * Derives a color variant from a base hex color by shifting hue and adjusting saturation/lightness.
 * If the base color is invalid, the original value is returned unchanged.
 */
const deriveColorFromBase = (
  baseColor: string,
  config: { hueShift: number; saturationDelta: number; lightnessDelta: number },
) => {
  const baseRgb = hexToRgb(baseColor);
  if (!baseRgb) {
    return baseColor;
  }

  const { h, s, l } = rgbToHsl(baseRgb.r, baseRgb.g, baseRgb.b);
  const derivedHue = (h + config.hueShift + 360) % 360;
  const derivedSaturation = clamp(s + config.saturationDelta, 20, 90);
  const derivedLightness = clamp(l + config.lightnessDelta, 20, 85);
  const derivedRgb = hslToRgb(derivedHue, derivedSaturation, derivedLightness);

  return rgbToHex(derivedRgb.r, derivedRgb.g, derivedRgb.b);
};

const deltaColorModifiers = {
  combined_delta_1h: { hueShift: 24, saturationDelta: 10, lightnessDelta: 14 },
  combined_delta_3h: { hueShift: 56, saturationDelta: 8, lightnessDelta: 10 },
  combined_delta_6h: { hueShift: 92, saturationDelta: 6, lightnessDelta: 8 },
  combined_delta_12h: { hueShift: 134, saturationDelta: 4, lightnessDelta: 5 },
  combined_delta_24h: { hueShift: 172, saturationDelta: 2, lightnessDelta: 2 },
  delta_1h: { hueShift: 28, saturationDelta: 12, lightnessDelta: 4 },
  delta_3h: { hueShift: 60, saturationDelta: 10, lightnessDelta: 1 },
  delta_6h: { hueShift: 96, saturationDelta: 8, lightnessDelta: -2 },
  delta_12h: { hueShift: 138, saturationDelta: 6, lightnessDelta: -5 },
  delta_24h: { hueShift: 176, saturationDelta: 4, lightnessDelta: -8 },
};

/**
 * Builds Highcharts styling config for delta series from a base power color.
 * Falls back to `DEFAULT_ENERGY_PLOT_MODEL_COLOR` when no valid color is provided.
 */
export const createDeltaSeriesStyling = (powerColor?: string | null) => {
  const baseColor = powerColor && hexToRgb(powerColor) ? powerColor : DEFAULT_ENERGY_PLOT_MODEL_COLOR;

  return {
    combined_delta_1h: {
      type: "area",
      dashStyle: "solid",
      color: deriveColorFromBase(baseColor, deltaColorModifiers.combined_delta_1h),
      fontSize: "12px",
      visible: true,
      showInLegend: true,
    },
    combined_delta_3h: {
      type: "area",
      dashStyle: "solid",
      color: deriveColorFromBase(baseColor, deltaColorModifiers.combined_delta_3h),
      fontSize: "12px",
      visible: true,
      showInLegend: true,
    },
    combined_delta_6h: {
      type: "area",
      dashStyle: "solid",
      color: deriveColorFromBase(baseColor, deltaColorModifiers.combined_delta_6h),
      fontSize: "12px",
      visible: true,
      showInLegend: true,
    },
    combined_delta_12h: {
      type: "area",
      dashStyle: "solid",
      color: deriveColorFromBase(baseColor, deltaColorModifiers.combined_delta_12h),
      fontSize: "12px",
      visible: true,
      showInLegend: true,
    },
    combined_delta_24h: {
      type: "area",
      dashStyle: "solid",
      color: deriveColorFromBase(baseColor, deltaColorModifiers.combined_delta_24h),
      fontSize: "12px",
      visible: true,
      showInLegend: true,
    },
    delta_1h: {
      type: "spline",
      dashStyle: "solid",
      color: deriveColorFromBase(baseColor, deltaColorModifiers.delta_1h),
      fontSize: "12px",
      visible: false,
      showInLegend: false,
    },
    delta_3h: {
      type: "spline",
      dashStyle: "solid",
      color: deriveColorFromBase(baseColor, deltaColorModifiers.delta_3h),
      fontSize: "12px",
      visible: false,
      showInLegend: false,
    },
    delta_6h: {
      type: "spline",
      dashStyle: "solid",
      color: deriveColorFromBase(baseColor, deltaColorModifiers.delta_6h),
      fontSize: "12px",
      visible: false,
      showInLegend: false,
    },
    delta_12h: {
      type: "spline",
      dashStyle: "solid",
      color: deriveColorFromBase(baseColor, deltaColorModifiers.delta_12h),
      fontSize: "12px",
      visible: false,
      showInLegend: false,
    },
    delta_24h: {
      type: "spline",
      dashStyle: "solid",
      color: deriveColorFromBase(baseColor, deltaColorModifiers.delta_24h),
      fontSize: "12px",
      visible: false,
      showInLegend: false,
    },
  };
};

/**
 * Builds reusable run and delta style palettes for previous energy runs.
 * The returned arrays are indexed by run order so each selected previous run
 * gets matching forecast and delta colors derived from the model power color.
 */
export const createDeltaSeriesStylingNew = (powerColor?: string | null) => {
  const baseColor = powerColor && hexToRgb(powerColor) ? powerColor : DEFAULT_ENERGY_PLOT_MODEL_COLOR;

  return {
    delta: [
      {
        type: "area",
        dashStyle: "solid",
        color: deriveColorFromBase(baseColor, deltaColorModifiers.combined_delta_1h), //TODO change names for modifier at later point
        fontSize: "12px",
        visible: true,
        showInLegend: true,
      },
      {
        type: "area",
        dashStyle: "solid",
        color: deriveColorFromBase(baseColor, deltaColorModifiers.combined_delta_3h),
        fontSize: "12px",
        visible: true,
        showInLegend: true,
      },
      {
        type: "area",
        dashStyle: "solid",
        color: deriveColorFromBase(baseColor, deltaColorModifiers.combined_delta_6h),
        fontSize: "12px",
        visible: true,
        showInLegend: true,
      },
      {
        type: "area",
        dashStyle: "solid",
        color: deriveColorFromBase(baseColor, deltaColorModifiers.combined_delta_12h),
        fontSize: "12px",
        visible: true,
        showInLegend: true,
      },
      {
        type: "area",
        dashStyle: "solid",
        color: deriveColorFromBase(baseColor, deltaColorModifiers.combined_delta_24h),
        fontSize: "12px",
        visible: true,
        showInLegend: true,
      },
    ],
    run: [
      {
        type: "spline",
        dashStyle: "solid",
        color: deriveColorFromBase(baseColor, deltaColorModifiers.delta_1h),
        fontSize: "12px",
        visible: true,
        showInLegend: true,
      },
      {
        type: "spline",
        dashStyle: "solid",
        color: deriveColorFromBase(baseColor, deltaColorModifiers.delta_3h),
        fontSize: "12px",
        visible: true,
        showInLegend: true,
      },
      {
        type: "spline",
        dashStyle: "solid",
        color: deriveColorFromBase(baseColor, deltaColorModifiers.delta_6h),
        fontSize: "12px",
        visible: true,
        showInLegend: true,
      },
      {
        type: "spline",
        dashStyle: "solid",
        color: deriveColorFromBase(baseColor, deltaColorModifiers.delta_12h),
        fontSize: "12px",
        visible: true,
        showInLegend: true,
      },
      {
        type: "spline",
        dashStyle: "solid",
        color: deriveColorFromBase(baseColor, deltaColorModifiers.delta_24h),
        fontSize: "12px",
        visible: true,
        showInLegend: true,
      },
    ],
  };
};
